import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { CheckCircle, XCircle, AlertTriangle, Clock, ChevronDown, ChevronRight, Plus, Loader2 } from 'lucide-react';
import type { PlatoSyncResult, PlatoSyncResultItem } from '@/types/plato';

interface PlatoSyncReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: PlatoSyncResult | null;
  onCreateConflictItem: (item: PlatoSyncResultItem) => Promise<void>;
  onRefreshSchedule?: () => void;
}

const PlatoSyncReportModal: React.FC<PlatoSyncReportModalProps> = ({
  isOpen,
  onClose,
  result,
  onCreateConflictItem,
  onRefreshSchedule,
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    added: true,
    updated: true,
    removed: true,
    conflicts: true,
    skipped: false,
    errors: false,
  });
  const [creatingItems, setCreatingItems] = useState<Set<string>>(new Set());
  const [createdItems, setCreatedItems] = useState<Set<string>>(new Set());

  if (!result) return null;

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleCreateConflictItem = async (item: PlatoSyncResultItem) => {
    if (!item.pendingData) return;

    setCreatingItems(prev => new Set(prev).add(item.platoAppointmentId));
    try {
      await onCreateConflictItem(item);
      setCreatedItems(prev => new Set(prev).add(item.platoAppointmentId));
    } catch (error) {
      console.error('Failed to create conflict item:', error);
    } finally {
      setCreatingItems(prev => {
        const next = new Set(prev);
        next.delete(item.platoAppointmentId);
        return next;
      });
    }
  };

  const handleClose = () => {
    // Always refresh the schedule after sync to show latest changes
    // (added items, updates, deletions, conflict items created)
    if (onRefreshSchedule) {
      onRefreshSchedule();
    }
    // Reset state
    setCreatedItems(new Set());
    setCreatingItems(new Set());
    onClose();
  };

  // Categorize items
  const addedItems = result.details.filter(d => d.status === 'added' || d.status === 'synced');
  const updatedItems = result.details.filter(d => d.status === 'updated');
  const removedItems = result.details.filter(d => d.status === 'removed');
  const conflictItems = result.details.filter(d => d.status === 'conflict');
  const skippedItems = result.details.filter(d => d.status === 'skipped');
  const errorItems = result.details.filter(d => d.status === 'error');

  const formatTime = (item: PlatoSyncResultItem) => {
    if (item.pendingData) {
      return `${item.pendingData.startParsed.time} - ${item.pendingData.endParsed.time}`;
    }
    if (item.timing) {
      return `${item.timing.startTime} - ${item.timing.endTime}`;
    }
    return 'Time unknown';
  };

  const formatDate = (item: PlatoSyncResultItem) => {
    if (item.pendingData) {
      return item.pendingData.startParsed.date;
    }
    if (item.timing) {
      return item.timing.date;
    }
    return '';
  };

  const renderSectionHeader = (
    section: string,
    title: string,
    count: number,
    icon: React.ReactNode,
    variant: 'default' | 'destructive' | 'secondary' | 'outline' = 'default'
  ) => (
    <CollapsibleTrigger asChild>
      <Button
        variant="ghost"
        className="w-full justify-between p-3 h-auto hover:bg-accent"
        onClick={() => toggleSection(section)}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium">{title}</span>
          <Badge variant={variant}>{count}</Badge>
        </div>
        {expandedSections[section] ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </Button>
    </CollapsibleTrigger>
  );

  const renderItemCard = (item: PlatoSyncResultItem, showCreateButton: boolean = false) => {
    const isCreating = creatingItems.has(item.platoAppointmentId);
    const isCreated = createdItems.has(item.platoAppointmentId);

    return (
      <div
        key={item.platoAppointmentId}
        className={`p-3 border rounded-md bg-card ${isCreated ? 'border-green-300 bg-green-50' : ''}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">
              {item.patientName || 'No patient name'}
            </div>
            {(item.pendingData || item.timing) && (
              <div className="text-xs text-muted-foreground mt-1">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(item)} {formatTime(item)}
                </div>
                {item.pendingData?.platoAppt?.prefix && (
                  <div className="mt-0.5">
                    Procedure: {item.pendingData.platoAppt.prefix}
                  </div>
                )}
              </div>
            )}
            {item.reason && (
              <div className="text-xs text-orange-600 mt-1 whitespace-pre-line">
                {item.reason}
              </div>
            )}
          </div>
          {showCreateButton && item.pendingData && !isCreated && (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => handleCreateConflictItem(item)}
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3 mr-1" />
                  Create
                </>
              )}
            </Button>
          )}
          {isCreated && (
            <Badge variant="default" className="bg-green-600 shrink-0">
              <CheckCircle className="h-3 w-3 mr-1" />
              Created
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result.success ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            Plato Sync Report
          </DialogTitle>
          <DialogDescription>
            Processed {result.totalProcessed} appointments
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 py-3 border-b">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{result.added}</div>
            <div className="text-xs text-muted-foreground">Added</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{result.updated || 0}</div>
            <div className="text-xs text-muted-foreground">Updated</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{result.deleted || 0}</div>
            <div className="text-xs text-muted-foreground">Removed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{result.conflicts}</div>
            <div className="text-xs text-muted-foreground">Conflicts</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-500">{result.skipped}</div>
            <div className="text-xs text-muted-foreground">Skipped</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{result.errors}</div>
            <div className="text-xs text-muted-foreground">Errors</div>
          </div>
        </div>

        {/* Details */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {/* Added Items */}
          {addedItems.length > 0 && (
            <Collapsible open={expandedSections.added}>
              {renderSectionHeader(
                'added',
                'Successfully Added',
                addedItems.length,
                <CheckCircle className="h-4 w-4 text-green-600" />,
                'default'
              )}
              <CollapsibleContent className="space-y-2 pl-2 pr-2 pb-2">
                {addedItems.map(item => renderItemCard(item))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Updated Items */}
          {updatedItems.length > 0 && (
            <Collapsible open={expandedSections.updated}>
              {renderSectionHeader(
                'updated',
                'Updated',
                updatedItems.length,
                <CheckCircle className="h-4 w-4 text-blue-600" />,
                'secondary'
              )}
              <CollapsibleContent className="space-y-2 pl-2 pr-2 pb-2">
                {updatedItems.map(item => renderItemCard(item))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Removed Items */}
          {removedItems.length > 0 && (
            <Collapsible open={expandedSections.removed}>
              {renderSectionHeader(
                'removed',
                'Removed',
                removedItems.length,
                <XCircle className="h-4 w-4 text-red-600" />,
                'destructive'
              )}
              <CollapsibleContent className="space-y-2 pl-2 pr-2 pb-2">
                {removedItems.map(item => renderItemCard(item))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Conflict Items */}
          {conflictItems.length > 0 && (
            <Collapsible open={expandedSections.conflicts}>
              {renderSectionHeader(
                'conflicts',
                'Conflicts (Requires Approval)',
                conflictItems.length,
                <AlertTriangle className="h-4 w-4 text-orange-600" />,
                'destructive'
              )}
              <CollapsibleContent className="space-y-2 pl-2 pr-2 pb-2">
                <p className="text-xs text-muted-foreground mb-2">
                  These items have time conflicts. Click "Create" to add them anyway.
                </p>
                {conflictItems.map(item => renderItemCard(item, true))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Skipped Items */}
          {skippedItems.length > 0 && (
            <Collapsible open={expandedSections.skipped}>
              {renderSectionHeader(
                'skipped',
                'Skipped',
                skippedItems.length,
                <Clock className="h-4 w-4 text-gray-500" />,
                'secondary'
              )}
              <CollapsibleContent className="space-y-2 pl-2 pr-2 pb-2">
                {skippedItems.map(item => renderItemCard(item))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Error Items */}
          {errorItems.length > 0 && (
            <Collapsible open={expandedSections.errors}>
              {renderSectionHeader(
                'errors',
                'Errors',
                errorItems.length,
                <XCircle className="h-4 w-4 text-red-600" />,
                'destructive'
              )}
              <CollapsibleContent className="space-y-2 pl-2 pr-2 pb-2">
                {errorItems.map(item => renderItemCard(item))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlatoSyncReportModal;
